import { Signal, signal, WritableSignal } from "@angular/core";
import { Subject, takeUntil, takeWhile, timer } from "rxjs";

export const TIMER_STORAGE_PREFIX: string = "timer:";

export const COUNTING_DIRECTIONS = {
  up: "up",
  down: "down"
} as const;
export type CountingDirection = (typeof COUNTING_DIRECTIONS)[ keyof typeof COUNTING_DIRECTIONS ];

const DEFAULT_TIMER_STRATEGY: TimerStrategy = {
  isPersistent: false
};

export interface TimerValues {
  startTime: number;
  duration: number;
}

interface PersistentTimerStrategy {
  readonly storageKey: string;
  isPersistent: true;
}

interface TransientTimerStrategy {
  isPersistent: false;
}

type TimerStrategy = PersistentTimerStrategy | TransientTimerStrategy;


export class Timer {
  private _strategy: TimerStrategy = DEFAULT_TIMER_STRATEGY;

  private _direction: CountingDirection = COUNTING_DIRECTIONS.down;

  private _intervalInMilliseconds: number = 1000;

  private _endTimer$: Subject<void> = new Subject<void>();

  private readonly _seconds: WritableSignal<number | null> = signal(null);

  public readonly seconds: Signal<number | null> = this._seconds.asReadonly();

  public constructor (strategy?: TimerStrategy, direction?: CountingDirection, interval?: number) {
    if (strategy)
      this._strategy = strategy;
    if (direction)
      this._direction = direction;
    if (interval)
      this._intervalInMilliseconds = interval;
  }

  public start (duration: number): void {
    const startTime: number = Date.now();

    const values: TimerValues = {
      duration,
      startTime
    };

    if (this._strategy.isPersistent)
      this.persistValues(values);

    this.run(values);
  }

  private run (values: TimerValues): void {
    const startTime: number = values.startTime;
    const endTime: number = startTime + values.duration;

    timer(0, this._intervalInMilliseconds)
      .pipe(
        takeWhile(() => Date.now() < endTime),
        takeUntil(this._endTimer$)
      )
      .subscribe({
        next: () => {
          if (this._direction === COUNTING_DIRECTIONS.down)
            this._seconds.set(Math.floor((endTime - Date.now()) / 1000));
          else if (values.startTime)
            this._seconds.set(Math.floor((Date.now() - startTime) / 1000));
        },
        complete: () => this.stop()
      });
  }

  public stop (): void {
    this._seconds.set(null);
    this._endTimer$.next();
    if (this._strategy.isPersistent)
      this.discontinueValues();
  }

  public persistValues (values: TimerValues): void {
    if (!this._strategy.isPersistent)
      throw new Error("Timer is not persistent");

    localStorage.setItem(TIMER_STORAGE_PREFIX + this._strategy.storageKey, JSON.stringify(values));
  }

  public discontinueValues (): void {
    if (!this._strategy.isPersistent)
      throw new Error("Timer is not persistent");

    localStorage.removeItem(TIMER_STORAGE_PREFIX + this._strategy.storageKey);
  }

  /**
   * Continues a timer with the values stored in the local storage. If the timer is not persistent or there are no valid values stored, the function will return false.
   *
   * @throws Error if the timer is not persistent.
   * @returns wether or not the timer is running at the moment.
   */
  public continue (): boolean {
    if (!this._strategy.isPersistent)
      throw new Error("Timer is not persistent");

    const valuesString: string | null = localStorage.getItem(TIMER_STORAGE_PREFIX + this._strategy.storageKey);

    if (!valuesString)
      return false;

    const values: TimerValues | null = JSON.parse(valuesString) as TimerValues | null;

    if (!values)
      return false;

    if (Date.now() > values.startTime + values.duration) {
      this.discontinueValues();
      return false;
    }

    this.run(values);

    return true;
  }
}
